'use client'

import { useAccount } from 'wagmi'
import { BONE_REWARDS_MERKLE, mainNetworkChainId } from '../constants'
import { TransactionResponse } from '@ethersproject/providers'
import { ChainId, ChainTokenMap } from '@shibaswap/sdk'
import { useEffect, useState } from 'react'
import { useShibaMerkleDistributorContract } from '../hooks/useContract'
import { calculateGasMargin, isAddress } from '../utils'
import useTransactionAdder from '../hooks/useTransactionAdder'
import { Contract } from '@ethersproject/contracts'


interface MerkleAmountAndProofs {
    uniqueId: string
    rewardTokenName: string
    amounts: any
    merkel_root: any
    distributorAddress: { [chainId in ChainId]?: string }
    tokenAddress: ChainTokenMap
    decimals: number
    fileName: string
    vesting: any
    disableLockInfo?: boolean
    showComingSoon?: boolean
    tooltip: string
}

interface UserClaimData {
    index: number
    amount: string
    proof: string[]
    flags?: {
        isSOCKS: boolean
        isLP: boolean
        isUser: boolean
    }
}


interface ClaimData {
    unclaimedAmount: string;
    claimHash?: string;
    index?: number; // Add index if needed
    amount?: string; // Add amount if needed
    proof?: string[]; // Add proof if needed
}


const CLAIM_PROMISES: { [key: string]: Promise<UserClaimData | null> } = {};

async function fetchClaim(account: string, chainId: ChainId, merkel: MerkleAmountAndProofs): Promise<UserClaimData | null> {
    if (!isAddress(account)) throw new Error('Invalid address');
    const key = `${chainId}:${account}:${merkel.uniqueId}`;

    if (!CLAIM_PROMISES[key]) {
        CLAIM_PROMISES[key] = fetch(`${merkel.merkel_root}${merkel.fileName}-${account}.json`)
            .then(response => response.json())
            .then(data => data ? {
                index: data.index,
                amount: data.amount,
                proof: data.proof
            } : null)
            .catch(error => {
                console.log('Error fetching claim data:', error);
                return null;
            });
    }

    return CLAIM_PROMISES[key];
}

export default function useClaimRewards() {
    const { chain, address } = useAccount();
    const [claimData, setClaimData] = useState<ClaimData | null>(null);
    const addTransaction = useTransactionAdder();
    let distributorContract: Contract | null = useShibaMerkleDistributorContract(BONE_REWARDS_MERKLE.distributorAddress);

    useEffect(() => {
        const fetchData = async () => {
            if (address && chain?.id === mainNetworkChainId) {
                const merkel = BONE_REWARDS_MERKLE;
                try {
                    const userClaimData = await fetchClaim(address, chain.id, merkel);
                    const unclaimedAmount = userClaimData ? userClaimData.amount : '0';
                    setClaimData({ unclaimedAmount, claimHash: undefined, index: userClaimData?.index, amount: userClaimData?.amount, proof: userClaimData?.proof });
                } catch (error) {
                    console.log('Error fetching claim data:', error);
                }
            }
        };
        if (chain?.id === mainNetworkChainId) {
            fetchData();
        }
    }, [address, chain]);

    const claimCallback = async (): Promise<string | undefined> => {
        if (!claimData || !address || !chain) return;
        const args = [claimData.index, address, claimData.amount, claimData.proof];
        if (!distributorContract) {
            console.log('Distributor contract is not available.');
            return undefined; // or handle the error as needed
        }
        try {
            const estimatedGasLimit = await distributorContract.estimateGas['claim'](...args, {});
            const response: TransactionResponse = await distributorContract.claim(...args, { value: null, gasLimit: calculateGasMargin(estimatedGasLimit) });

            addTransaction(response, {
                summary: `Claimed ${claimData.unclaimedAmount} ${BONE_REWARDS_MERKLE.rewardTokenName}`,
                claim: { recipient: address }
            });
            return response.hash;
        } catch (error) {
            console.log('Error during claim:', error);
            return undefined;
        }
    };

    return { claimData, claimCallback };
}
